import React, { useMemo, useState } from 'react'
import { Page } from '../../components/page'
import styled from 'styled-components'
import { BusinessCard } from '../../components/business-card'
import { Button, List } from 'antd-mobile'
import { Visited } from '../../components/visited'
import { useCallNumber, useChangeTitle, useCopyToClipboard, useLoading, useNavigate, useTrackVisit } from '../../hooks'
import { HorizontalScroll } from '../../components/horizontal-scroll'
import { Icon } from '../../components/icon'
import { useLocalBusinessCard } from '../../store'
import { useCompanyInfo, useMenus, useVisitors } from '../../api/query-hooks'
import { Menu } from '../../api/backend'
import { DirectToButton } from '../../components/direct-to-button'
import { Skeleton } from '../../components/skeleton'
import { PopImage } from '../../components/pop-image'
import { Footer } from '../../components/footer'

const CardContainer = styled.div`
  width: 100%;
  padding-top: 1.25rem;
  margin-bottom: 1.25rem;
  background: linear-gradient(180deg, #EDEDED 0%, #FFFFFF 100%);
`
const ButtonsContainer = styled.div`
  width: 100%;
  padding: 0 1.25rem;
  margin-bottom: 1.25rem;
  
  display: flex;
  flex-direction: row;
  justify-content: start;
  align-items: start;
  & > * + * {
    margin-left: 0.5rem;
  }
  & > button {
    flex: 1 1 1px;
    font-size: 1rem;
  }
`
const ContactsContainer = styled(HorizontalScroll)`
  padding: 0 1.25rem;
  margin-bottom: 1.25rem;
  & > * + * {
    margin-left: 0.25rem;
  }
`
const Contact = styled(Button).attrs<{ icon: string, title: string, text: string }>(props => ({
  size: 'small',
  children: (
    <div className="contact">
      <div className="icon"><Icon code={props.icon} size="0.75rem" color="theme" /></div>
      <div className="title">{props.title}</div>
      <div className="text">{props.text}</div>
    </div>
  )
}))<{ icon: string, title: string, text: string }>`
  padding: 0.75rem;
  .contact {
    display: grid;
    grid-template-columns: 0.75rem auto;
    grid-template-rows: 0.875rem auto;
    grid-gap: 0.5rem;
    .icon {
      grid-column: 1 / 2;
      grid-row: 1 / 2;
    }
    .title {
      grid-column: 2 / 3;
      grid-row: 1 / 2;
      font-size: 0.75rem;
      line-height: 1.5;
      text-align: left;
    }
    .text {
      grid-column: 2 / 3;
      grid-row: 2 / 3;
      height: 1.25rem;
      font-size: 0.875rem;
      line-height: 1.5;
      text-align: left;
      opacity: 0.6;
    }
  }
`
const VisitedContainer = styled.div`
  width: 100%;
  padding: 0 1.25rem;
  margin-bottom: 1.25rem;
`
const Divider = styled.div`
  height: 0.5rem;
  width: 100%;
  background-color: ${props => props.theme.colors.grayLight};
`
const StyledList = styled(List)`
  border-top: none;
`
const ListItem =styled(List.Item)`
  padding-left: 1.25rem;
  .adm-list-item-content {
    padding: 1.25rem 1.25rem 1.25rem 0;
  }
  .adm-list-item-content-prefix {
    display: flex;
    align-items: center;
  }
`
const Extra = styled.span`
  color: ${props => props.theme.colors.textTip};
  font-size: 0.875rem;
`
const ListTitle = styled.span`
  color: ${props => props.theme.colors.textLight};
  font-size: 0.875rem;
`

export const Card: React.VFC = () => {
  useTrackVisit()
  useLoading(true, {
    query: {
      predicate: ({ queryKey }) => {
        if (Array.isArray(queryKey)) {
          return queryKey[0] === 'businessCard'
        } else {
          return queryKey === 'menus'
        }
      }
    }
  })
  const businessCard = useLocalBusinessCard()
  const companyInfo = useCompanyInfo()
  const value = (businessCard && companyInfo) ? { businessCard, companyInfo } : undefined
  useChangeTitle(businessCard)
  const call = useCallNumber()
  const menus = useMenus()
  const visitors = useVisitors()
  const copyToClipboard = useCopyToClipboard()
  const [showDirection, setShowDirection] = useState(false)
  const [popImageUrl, setPopImageUrl] = useState<string>('')
  return (
    <Skeleton
      value={value}
      render={({
        businessCard: {
          addWeixinQrCode,
          portrait,
          avatar,
          userName,
          cellphone,
          email,
          address,
          companyPosition,
          wechat,
          vcardUrl,
          chatUrl,
        },
        companyInfo: {
          squareLogo,
          companyName
        }
      }) => (
        <Page>
          <DirectToButton visible={showDirection} onClose={() => setShowDirection(false)} />
          <PopImage
            visible={!!popImageUrl}
            onClose={() => setPopImageUrl('')}
            url={popImageUrl} />
          <CardContainer>
            <BusinessCard
              name={userName}
              title={companyPosition}
              avatar={portrait ?? avatar}
              phone={cellphone}
              email={email}
              address={address}
              company={{
                name: companyName,
                logo: squareLogo
              }}
            />
          </CardContainer>
          <ButtonsContainer>
            <Button
              color="primary"
              fill="outline"
              size="large"
              onClick={() => setShowDirection(true)}
            >
              分享名片
            </Button>
            <Button
              color="primary"
              fill="solid"
              size="large"
              onClick={() => setPopImageUrl(vcardUrl)}
            >
              保存至通讯录
            </Button>
          </ButtonsContainer>
          <ContactsContainer>
            <Contact
              icon="24gf-phoneBubble"
              title="拨打电话"
              text={cellphone}
              onClick={() => call(cellphone)}
            />
            <Contact
              icon="weixin"
              title="加我微信"
              text={wechat}
              onClick={() => setPopImageUrl(addWeixinQrCode)}
            />
            <Contact
              icon="paichu1"
              title="在线联系"
              text={userName}
              onClick={() => window.open(chatUrl, "_blank")}
            />
            <Contact
              icon="youjian"
              title="邮箱"
              text={email}
              onClick={() => copyToClipboard(email)}
            />
            <Contact
              icon="didian2"
              title="地址"
              text={address}
              onClick={() => copyToClipboard(address)}
            />
          </ContactsContainer>
          <VisitedContainer>
            <Visited
              avatars={visitors?.avatars ?? []}
              count={visitors?.count ?? 0}
            />
          </VisitedContainer>
          <Divider />
          <StyledList>
            {menus.map(menu => <MenuItem key={menu.businessCardMenuId} menu={menu} />)}
          </StyledList>
          <Footer />
        </Page>
      )}
    />
  )
}

interface MenuItemProps {
  menu: Menu
}
const MenuItem: React.VFC<MenuItemProps> = ({ menu }) => {
  const navigate = useNavigate()
  const { onClick, icon } = useMemo(() => {
    switch (menu.type) {
      case 'FILE':
        return {
          icon: 'a-bijibenbiji',
          onClick: () => navigate(`/files/${menu.businessCardMenuId}`)
        }
      case 'IMAGE':
        return {
          icon: 'xiangce',
          onClick: () => navigate(`/images/${menu.businessCardMenuId}`)
        }
      case 'URL':
        return {
          icon: 'paihangbang',
          onClick: () => window.open(menu.url)
        }
      case 'VIDEO':
        return {
          icon: 'a-zhibokecheng',
          onClick: () => navigate(`/videos/${menu.businessCardMenuId}`)
        }
      default:
        throw new Error(`unsupported menu type ${menu.type}`)
    }
  }, [menu, navigate])
  return (
    <ListItem
      extra={<Extra>{menu.fileCountStr}</Extra>}
      prefix={<Icon code={icon} size="1.25rem" color="theme" />}
      onClick={onClick}
    >
      <ListTitle>{menu.menuName}</ListTitle>
    </ListItem>
  )
}
